import { Link } from "react-router-dom";

// Replaces the "Leads by Status" donut. Status is an ordered flow, so a pie
// hides the only thing worth seeing: where people drop out.
//
// Status holds where a lead is NOW, not every stage it passed through — a
// scheduled lead is no longer counted as contacted. So a funnel stage is the
// count of leads that reached it *or got further*, inferred from the statuses
// that can only follow it:
//
//   reached contacted  = contacted + scheduled + converted + no-show
//   reached scheduled  = scheduled + converted + no-show   (you can only
//                        no-show an appointment you had)
//   reached converted  = converted
//
// "Lost" is deliberately not placed in the funnel: the status does not record
// which stage the lead was lost at, so any placement would be a guess.
//
// One colour throughout. Bar length and the stage labels carry the ordering;
// colouring stages differently would imply they are separate categories.
const FUNNEL_STAGES = [
  { key: "arrived", label: "Arrived", includes: null },
  { key: "contacted", label: "Contacted", includes: ["contacted", "scheduled", "converted", "noshow"] },
  { key: "scheduled", label: "Scheduled", includes: ["scheduled", "converted", "noshow"] },
  { key: "converted", label: "Converted", includes: ["converted"] },
];

const StatusFunnel = ({ leadStatusData = [], buildLeadsLink }) => {
  const byKey = Object.fromEntries(
    leadStatusData.map((row) => [row.key || row.name, row.value]),
  );
  const total = leadStatusData.reduce((sum, row) => sum + row.value, 0);

  const stages = FUNNEL_STAGES.map((stage) => ({
    ...stage,
    value: stage.includes
      ? stage.includes.reduce((sum, key) => sum + (byKey[key] || 0), 0)
      : total,
  }));

  const lost = byKey.lost || 0;
  const noshow = byKey.noshow || 0;
  const top = stages[0].value;

  if (total === 0) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Lead funnel
        </h2>
        <div className="text-center py-10">
          <p className="text-gray-500 text-sm">No leads in this date range</p>
          <p className="text-gray-400 text-xs mt-1">
            The funnel shows where leads drop out once they start arriving.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">Lead funnel</h2>
      <p className="text-sm text-gray-500 mb-5">
        How far leads get. Each stage counts everyone who reached it or went
        further.
      </p>

      <div className="space-y-1">
        {stages.map((stage, index) => {
          const width =
            stage.value === 0 || top === 0
              ? 0
              : Math.max((stage.value / top) * 100, 2);
          const previous = index > 0 ? stages[index - 1] : null;
          const dropped = previous ? previous.value - stage.value : null;

          // "Arrived" is every status, so it has no single status to filter by.
          const to = stage.includes
            ? buildLeadsLink({ status: stage.includes })
            : buildLeadsLink({});

          return (
            <div key={stage.key}>
              <Link
                to={to}
                className="group flex items-center gap-3 py-1"
                title={`${stage.value} reached ${stage.label.toLowerCase()}`}
              >
                <span className="w-24 flex-shrink-0 text-sm text-gray-600 text-right">
                  {stage.label}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="block h-7 rounded-r bg-teal-700 group-hover:bg-teal-800 transition-colors"
                    style={{ width: `${width}%` }}
                  />
                </span>
                <span className="w-12 flex-shrink-0 text-sm font-semibold text-gray-800">
                  {stage.value}
                </span>
              </Link>
              {dropped !== null && dropped > 0 && (
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="w-24 flex-shrink-0" />
                  <span>
                    −{dropped} stopped before {stage.label.toLowerCase()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-8 mt-5 pt-4 border-t border-border">
        <Link to={buildLeadsLink({ status: "lost" })} className="group">
          <span className="block text-lg font-semibold text-gray-700 group-hover:text-teal-700">
            {lost}
          </span>
          <span className="text-xs text-gray-500">
            Not interested
            <span className="block text-gray-400">stage not recorded</span>
          </span>
        </Link>
        <Link to={buildLeadsLink({ status: "noshow" })} className="group">
          <span className="block text-lg font-semibold text-gray-700 group-hover:text-teal-700">
            {noshow}
          </span>
          <span className="text-xs text-gray-500">
            No show
            <span className="block text-gray-400">counted as scheduled</span>
          </span>
        </Link>
      </div>
    </div>
  );
};

export default StatusFunnel;
